import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TableCustom from '../../../../components/v2/TableCustom';
import EmptyCreatePage from '../../../../components/v2/EmptyCreatePage';
import { Button } from '../../../../empreenderUI/ui/Button';
import { Tabs, TabsList, TabsTrigger } from '../../../../empreenderUI/ui/Tabs/Tabs';
import { Plus, RefreshCw, ShoppingBag, X } from 'lucide-react';
import { translations } from '../../../../utils/language';
import VariantsExpandPanel, { getVariantsCache, variantKey } from './VariantsExpandPanel';
import ModalAddContent from './ModalAddContent';
import ModalUploadAnexoNew from '../modal-upload-anexo-new';
import ModalCadLinkNew from '../modal-cad-link-new';
import ModalAnexarGaleria from '../modal-anexar-galeria-new';
import ModalChavesCodigosNew from '../modal-chaves-codigos-new';
import ModalUploadPreview from '../modal-upload-preview';
import ModalFeedSinc from '../tailwind/modal-feed-sinc';
import ModalFeedCompleted from '../../../../components/tailwind/modal-feed-completed';

export default function ProductsTable({ search, setSearch, goSearch, mediasFilter, setMediasFilter, setPage, page }) {
  const dispatch = useDispatch();
  const products = useSelector((state) => state.reducerProducts);
  const language = useSelector((state) => state.reducerLanguage);
  const lang = language?.data || 'pt';

  const [dataProducts, setDataProducts] = useState([]);
  const [listPending, setListPending] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [refreshMap, setRefreshMap] = useState({});
  const [variantSelection, setVariantSelection] = useState({
    productId: null,
    keys: new Set(),
  });

  const setProductVariantSelection = (productId, keys) => {
    const nextKeys = keys instanceof Set ? keys : new Set(keys || []);
    if (nextKeys.size === 0) {
      setVariantSelection((prev) => (prev.productId === productId ? { productId: null, keys: new Set() } : prev));
      return;
    }
    setVariantSelection({ productId, keys: nextKeys });
  };

  const handleExpandedChange = (keys) => {
    setExpandedKeys(keys);
    setVariantSelection((prev) => {
      if (prev.productId != null && !keys.includes(prev.productId)) {
        return { productId: null, keys: new Set() };
      }
      return prev;
    });
  };

  const selectionProduct = useMemo(() => {
    if (variantSelection.productId == null) return null;
    return dataProducts.find((item) => item?.products?.product_id === variantSelection.productId) || null;
  }, [dataProducts, variantSelection.productId]);

  const selectionMeta = useMemo(() => {
    if (variantSelection.productId == null || variantSelection.keys.size === 0) {
      return null;
    }

    const cache = getVariantsCache(variantSelection.productId);
    const items = cache?.items || [];
    const selectedVariants = items.filter((item, index) => variantSelection.keys.has(variantKey(item, index)));
    const total = cache?.total || Number(selectionProduct?.variants) || items.length || variantSelection.keys.size;
    const selectedCount = variantSelection.keys.size;
    const selectionLabel = selectedCount === 1 ? selectedVariants[0]?.variants?.name || `1 ${translations['products-variant-singular'][lang]}` : `${selectedCount} ${translations['products-variants-label'][lang]}`;

    return {
      productName: selectionProduct?.products?.name || translations['products-th-produto'][lang],
      selectedCount,
      total,
      selectionLabel,
      selectedVariants,
    };
  }, [selectionProduct, variantSelection, lang]);

  const [chooserOpen, setChooserOpen] = useState(false);
  const [chooserContext, setChooserContext] = useState({ product: null, variants: [], label: '' });

  const [visibleModalAnexo, setVisibleModalAnexo] = useState(false);
  const [visibleModalLink, setVisibleModalLink] = useState(false);
  const [visibleModalGaleria, setVisibleModalGaleria] = useState(false);
  const [visibleModalChaves, setVisibleModalChaves] = useState(false);
  const [editLicense, setEditLicense] = useState(null);
  const [visibleModalPreview, setVisibleModalPreview] = useState(false);
  const [visibleModalFeedSinc, setVisibleModalFeedSinc] = useState(false);
  const [visibleModalCompletedFeed, setVisibleModalCompletedFeed] = useState(false);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [dataModalSincCompleted, setDataModalSincCompleted] = useState([]);
  const [productLoadingId, setProductLoadingId] = useState(null);
  const [searchFiles, setSearchFiles] = useState('');
  const [pageFiles, setPageFiles] = useState(1);
  const [dataFilesTrue, setDataFilesTrue] = useState([]);

  useEffect(() => {
    // Soft refresh: mantém linhas visíveis enquanto o saga recarrega
    if (products.loading) return;
    const list = products?.data?.data?.data;
    if (Array.isArray(list)) {
      setDataProducts(list);
      setListPending(false);
    }
  }, [products]);

  const bumpRefresh = (productId) => {
    setRefreshMap((prev) => ({ ...prev, [productId]: (prev[productId] || 0) + 1 }));
  };

  const refreshExpanded = useCallback(() => {
    expandedKeys.forEach((id) => bumpRefresh(id));
    goSearch?.();
  }, [expandedKeys, goSearch]);

  const openChooser = (product, variants, label) => {
    setChooserContext({ product, variants, label });
    setChooserOpen(true);
  };

  const prepareVariantsControl = (variants) => {
    dispatch({ type: 'VARIANTS_CONTROL_ADD', payload: variants });
  };

  const handleChooserSelect = (kind) => {
    const { variants } = chooserContext;
    prepareVariantsControl(variants);
    setChooserOpen(false);

    if (kind === 'file') setVisibleModalAnexo(true);
    if (kind === 'gallery') {
      goSearchFiles();
      setVisibleModalGaleria(true);
    }
    if (kind === 'link') setVisibleModalLink(true);
    if (kind === 'key') setVisibleModalChaves(true);
  };

  const goSearchFiles = () => {
    dispatch({
      type: 'SAGA_LIST_MEDIA',
      payload: { search: searchFiles, perPage: '10', page: pageFiles },
    });
  };

  useEffect(() => {
    if (visibleModalGaleria) goSearchFiles();
  }, [pageFiles]);

  const syncProduct = async (item) => {
    setSelectedProducts([item]);
    setVisibleModalFeedSinc(true);
    setProductLoadingId(item.products.id);
    try {
      const token = JSON.parse(localStorage.getItem('depoimentos@login'))?.access_token.token;
      const url = `${process.env.REACT_APP_API_URL}/products/update`;
      const headers = new Headers({
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      });
      await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          product: item.products,
          integration: JSON.parse(localStorage.getItem('depoimentos@login'))?.integration,
        }),
      });
      setDataModalSincCompleted((prev) => [...prev, item.products.id]);
    } catch (e) {
      console.error(e);
    } finally {
      setProductLoadingId(null);
      setVisibleModalFeedSinc(false);
      setVisibleModalCompletedFeed(true);
    }
  };

  const syncAllVisible = async () => {
    if (!dataProducts.length) return;
    setSelectedProducts(dataProducts);
    setVisibleModalFeedSinc(true);
    for (const item of dataProducts) {
      try {
        setProductLoadingId(item.products.id);
        const token = JSON.parse(localStorage.getItem('depoimentos@login'))?.access_token.token;
        const url = `${process.env.REACT_APP_API_URL}/products/update`;
        const headers = new Headers({
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        });
        await fetch(url, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            product: item.products,
            integration: JSON.parse(localStorage.getItem('depoimentos@login'))?.integration,
          }),
        });
        setDataModalSincCompleted((prev) => [...prev, item.products.id]);
      } catch (e) {
        console.error(e);
      } finally {
        setProductLoadingId(null);
      }
    }
    setVisibleModalFeedSinc(false);
    setVisibleModalCompletedFeed(true);
  };

  const closeCompletedModal = () => {
    setVisibleModalCompletedFeed(false);
    goSearch?.();
    setDataModalSincCompleted([]);
    setSelectedProducts([]);
  };

  const columns = useMemo(
    () => [
      { label: translations['products-th-produto']?.[lang] || 'Produto', className: 'w-auto' },
      { label: translations['products-th-variacoes']?.[lang] || 'Variantes', className: 'w-24' },
      { label: translations['products-th-arquivos']?.[lang] || 'Conteúdos', className: 'w-24' },
      { label: translations['Ações'][lang], className: 'w-16' },
    ],
    [lang],
  );

  const toggleExpand = (productId) => {
    setExpandedKeys((prev) => (prev.includes(productId) ? prev.filter((k) => k !== productId) : [...prev, productId]));
  };

  const rows = useMemo(
    () =>
      dataProducts.map((item) => {
        const productId = item?.products?.product_id;
        return [
          <button key='name' type='button' className='flex items-center gap-3 min-w-0 max-w-full text-left w-full overflow-hidden' onClick={() => toggleExpand(productId)}>
            {item.products?.image_url ? (
              <img src={item.products.image_url} alt='' className='size-11 rounded-lg object-cover shrink-0 bg-violet-50' />
            ) : (
              <span className='size-11 rounded-lg bg-violet-50 grid place-items-center shrink-0'>
                <ShoppingBag className='size-5 text-violet-400' />
              </span>
            )}
            <div className='min-w-0 flex-1 overflow-hidden'>
              <div className='font-medium text-gray-900 leading-snug line-clamp-2 break-words max-w-[500px]' title={item.products?.name}>
                {item.products?.name}
              </div>
              <div className='text-xs text-gray-500 mt-0.5 truncate'>
                {item.variants > 0 ? item.variants : 1} {translations['products-variants-label'][lang]} · {item.media || 0} {translations['products-files-label'][lang]}
              </div>
            </div>
          </button>,
          <span key='variants' className='text-sm text-gray-600'>
            {item.variants > 0 ? item.variants : 1}
          </span>,
          <span key='media' className='text-sm text-gray-600'>
            {item.media || 0}
          </span>,
          <Button
            key='sync'
            variant='outline'
            size='icon'
            className='border-gray-200'
            title={translations['products-sincronizar-produto'][lang]}
            onClick={(e) => {
              e.stopPropagation();
              syncProduct(item);
            }}
          >
            <RefreshCw className='size-4' />
          </Button>,
        ];
      }),
    [dataProducts, lang],
  );

  const pagination = products?.data?.data?.meta;
  const storeList = products?.data?.data?.data;
  const isListResolved = Array.isArray(storeList);
  const hasActiveQuery = Boolean(search) || (mediasFilter && mediasFilter !== 'all');
  const showEmptyPage = !products.loading && !listPending && isListResolved && storeList.length === 0 && !hasActiveQuery;

  const handleFilterChange = (value) => {
    setListPending(true);
    setPage?.(1);
    setMediasFilter?.(value);
  };

  const handlePageChange = (nextPage) => {
    setListPending(true);
    setPage?.(nextPage);
  };

  const filterSelect = (
    <Tabs value={mediasFilter || 'all'} onValueChange={handleFilterChange} className='min-w-0 w-full sm:w-auto'>
      <TabsList className='w-full sm:w-auto'>
        <TabsTrigger value='all' className='flex-none px-3 text-sm whitespace-nowrap'>
          {translations['products-todos-produtos'][lang]}
        </TabsTrigger>
        <TabsTrigger value='not' className='flex-none px-3 text-sm whitespace-nowrap'>
          {translations['products-filtro-sem-arquivos'][lang]}
        </TabsTrigger>
        <TabsTrigger value='only' className='flex-none px-3 text-sm whitespace-nowrap'>
          {translations['products-filtro-com-arquivos'][lang]}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );

  return (
    <>
      <ModalAddContent open={chooserOpen} onOpenChange={setChooserOpen} contextLabel={chooserContext.label} onSelect={handleChooserSelect} />

      <ModalUploadAnexoNew
        setVisible={setVisibleModalAnexo}
        visible={visibleModalAnexo}
        goSearch={refreshExpanded}
        eyebrow={chooserContext.product?.products?.name}
        contextLabel={chooserContext.label}
      />

      <ModalCadLinkNew setVisible={setVisibleModalLink} visible={visibleModalLink} goSearch={refreshExpanded} eyebrow={chooserContext.product?.products?.name} contextLabel={chooserContext.label} />

      <ModalChavesCodigosNew
        setVisible={(v) => {
          setVisibleModalChaves(v);
          if (!v) setEditLicense(null);
        }}
        visible={visibleModalChaves}
        goSearch={refreshExpanded}
        eyebrow={chooserContext.product?.products?.name}
        contextLabel={chooserContext.label}
        initialLicense={editLicense}
      />

      <ModalAnexarGaleria
        setVisible={setVisibleModalGaleria}
        visible={visibleModalGaleria}
        searchFiles={searchFiles}
        setSearchFiles={setSearchFiles}
        goSearchFiles={goSearchFiles}
        pageFiles={pageFiles}
        setPageFiles={setPageFiles}
        dataFilesTrue={dataFilesTrue}
        handleClick={() => {}}
        setDataFilesTrue={setDataFilesTrue}
        goSearch={refreshExpanded}
        eyebrow={chooserContext.product?.products?.name}
        contextLabel={chooserContext.label}
      />

      <ModalUploadPreview setVisible={setVisibleModalPreview} visible={visibleModalPreview} goSearch={refreshExpanded} eyebrow={chooserContext.product?.products?.name} contextLabel={chooserContext.label} />

      <ModalFeedSinc
        setVisible={setVisibleModalFeedSinc}
        visible={visibleModalFeedSinc}
        selectedProducts={selectedProducts}
        dataModalSincCompleted={dataModalSincCompleted}
        setDataModalSincCompleted={setDataModalSincCompleted}
        productLoadingId={productLoadingId}
        goSearch={goSearch}
        handleClick={() => {}}
      />

      <ModalFeedCompleted setVisible={setVisibleModalCompletedFeed} visible={visibleModalCompletedFeed} title={translations['products-sinc-completa']?.[lang]} desc={translations['products-sinc-completa-desc']?.[lang]} close={closeCompletedModal} />

      {showEmptyPage ? (
        <EmptyCreatePage
          showAlert={false}
          showButtons={false}
          title={translations['page-products-title']?.[lang] || 'Produtos'}
          description={translations['page-products-description']?.[lang] || 'Ainda não há produtos para mostrar.'}
          benefits={[
            translations['products-empty-benefit-1'][lang],
            translations['products-empty-benefit-2'][lang],
            translations['products-empty-benefit-3'][lang],
            translations['products-empty-benefit-4'][lang],
            translations['products-empty-benefit-5'][lang],
          ]}
        />
      ) : (
        <div className='px-4 sm:px-6 lg:px-8 w-full max-w-full min-w-0 box-border'>
          <div className='flex items-start justify-between gap-4 mb-6 min-w-0'>
            <div className='min-w-0'>
              <h1 className='text-2xl font-semibold tracking-tight text-gray-900'>{translations['page-products-title']?.[lang] || 'Produtos'}</h1>
              <p className='mt-2 text-sm text-gray-500'>{translations['products-subtitle'][lang]}</p>
            </div>
          </div>

          <TableCustom
            className='w-full max-w-full min-w-0'
            title={null}
            setSearch={setSearch}
            searchValue={search}
            searchPlaceholder={translations['products-pesquisar-nome']?.[lang] || 'Pesquisar produto…'}
            onSearchSubmit={() => {
              setListPending(true);
              setPage?.(1);
              goSearch?.();
            }}
            columns={columns}
            data={dataProducts}
            rows={rows}
            pagination={pagination}
            isLoading={listPending || (!!products.loading && dataProducts.length === 0)}
            getData={handlePageChange}
            filters={filterSelect}
            actionTable={[
              {
                label: (
                  <>
                    <RefreshCw className='size-4' />
                    {translations['products-sincronizar']?.[lang] || 'Sincronizar'}
                  </>
                ),
                onClick: syncAllVisible,
                variant: 'outline',
                visible: true,
              },
            ]}
            expandedKeys={expandedKeys}
            onExpandedChange={handleExpandedChange}
            getRowKey={(item) => item?.products?.product_id}
            renderExpandedRow={(item) => {
              const productId = item?.products?.product_id;
              const isSelectionOwner = variantSelection.productId === productId;
              return (
                <VariantsExpandPanel
                  product={item}
                  refreshToken={refreshMap[productId] || 0}
                  selectedKeys={isSelectionOwner ? variantSelection.keys : new Set()}
                  onSelectedKeysChange={(keys) => setProductVariantSelection(productId, keys)}
                  onAddContent={(selectedVariants) => {
                    const label = selectedVariants.length === 1 ? `${item.products?.name} · ${selectedVariants[0]?.variants?.name || translations['products-variant-singular'][lang]}` : `${item.products?.name} · ${selectedVariants.length} ${translations['products-variants-label'][lang]}`;
                    openChooser(item, selectedVariants, label);
                  }}
                  onEditLicense={({ license, variant }) => {
                    const label = `${item.products?.name} · ${variant?.variants?.name || translations['products-variant-singular'][lang]}`;
                    setChooserContext({ product: item, variants: [variant], label });
                    prepareVariantsControl([variant]);
                    setEditLicense(license);
                    setVisibleModalChaves(true);
                  }}
                  onAddPreview={(selectedVariants) => {
                    const label = selectedVariants.length === 1 ? `${item.products?.name} · ${selectedVariants[0]?.variants?.name || translations['products-variant-singular'][lang]}` : `${item.products?.name} · ${selectedVariants.length} ${translations['products-variants-label'][lang]}`;
                    setChooserContext({ product: item, variants: selectedVariants, label });
                    prepareVariantsControl(selectedVariants);
                    setVisibleModalPreview(true);
                  }}
                />
              );
            }}
          />
        </div>
      )}

      {selectionMeta && (
        <div className='fixed bottom-4 left-1/2 z-50 w-[min(920px,calc(100%-2rem))] -translate-x-1/2 rounded-xl border border-slate-700/80 bg-slate-900/95 px-4 sm:px-5 py-3 shadow-[0_12px_40px_rgba(0,0,0,0.45)] backdrop-blur-sm'>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div className='flex flex-wrap items-center gap-3 min-w-0 flex-1'>
              <div className='min-w-0 max-w-[220px] sm:max-w-[280px]'>
                <p className='text-sm font-semibold text-white truncate' title={selectionMeta.productName}>
                  {selectionMeta.productName}
                </p>
                <p className='text-xs text-slate-400 truncate'>{selectionMeta.selectionLabel} {translations['products-selected-suffix'][lang]}</p>
              </div>
              <div className='flex flex-wrap items-center gap-2 min-w-0'>
                <Button
                  variant='outline'
                  className='border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white hover:border-slate-500'
                  onClick={() => {
                    const selectedVariants = selectionMeta.selectedVariants;
                    if (!selectedVariants.length || !selectionProduct) return;
                    const label = selectedVariants.length === 1 ? `${selectionMeta.productName} · ${selectedVariants[0]?.variants?.name || translations['products-variant-singular'][lang]}` : `${selectionMeta.productName} · ${selectedVariants.length} ${translations['products-variants-label'][lang]}`;
                    openChooser(selectionProduct, selectedVariants, label);
                  }}
                >
                  <Plus className='size-4' />
                  {translations['products-add-content'][lang]}
                </Button>
                <Button
                  variant='outline'
                  className='border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-white hover:border-slate-500'
                  onClick={() => {
                    const selectedVariants = selectionMeta.selectedVariants;
                    if (!selectedVariants.length || !selectionProduct) return;
                    const label = selectedVariants.length === 1 ? `${selectionMeta.productName} · ${selectedVariants[0]?.variants?.name || translations['products-variant-singular'][lang]}` : `${selectionMeta.productName} · ${selectedVariants.length} ${translations['products-variants-label'][lang]}`;
                    setChooserContext({
                      product: selectionProduct,
                      variants: selectedVariants,
                      label,
                    });
                    prepareVariantsControl(selectedVariants);
                    setVisibleModalPreview(true);
                  }}
                >
                  {translations['products-add-pdf'][lang]}
                </Button>
              </div>
            </div>
            <div className='flex items-center gap-3 shrink-0'>
              <span className='text-xs text-slate-400 hidden sm:inline'>
                {selectionMeta.selectedCount} {translations['products-pagination-de'][lang]} {selectionMeta.total} {translations['products-variants-label'][lang]}
              </span>
              <Button
                type='button'
                variant='secondary'
                size='icon'
                className='size-8 shrink-0 border-0 bg-slate-700 text-slate-200 hover:bg-slate-600 hover:text-white'
                aria-label={translations['products-clear-selection'][lang]}
                onClick={() => setVariantSelection({ productId: null, keys: new Set() })}
              >
                <X className='size-4' />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
